import { z } from 'zod';
import { IsoDateTime, Uuid, Version } from './common.js';
/**
 * Modules HOREVIA (ADR-014, DS-02) : un établissement n'active et ne paie que
 * les modules dont il a besoin. Le catalogue est versionné ici (source de
 * vérité, comme les permissions) et synchronisé en base par le seed et la
 * migration 0010. Un module inactif = routes 404 et navigation absente.
 */
export const MODULE_CODES = [
    'hotel',
    'restaurant',
    'bar',
    'pos',
    'inventory',
    'crm',
    'analytics',
    'housekeeping',
    'booking',
    'pay',
];
export const ModuleCode = z.enum(MODULE_CODES);
export const ModuleStatus = z.enum(['AVAILABLE', 'COMING_SOON']);
const m = (code, name, description, status, dependsOn, sortOrder) => ({ code, name, description, status, dependsOn, sortOrder });
/** Catalogue des modules (libellés en français : langue par défaut de l'administration). */
export const MODULE_CATALOG = [
    m('hotel', 'Horevia Hotel', 'Hébergements, disponibilités, réservations, séjours, check-in/out, conformité, facturation des séjours.', 'AVAILABLE', [], 10),
    m('pos', 'Horevia POS', 'Moteur de caisse : encaissement, tickets, moyens de paiement, points de vente.', 'AVAILABLE', [], 20),
    m('restaurant', 'Horevia Restaurant', 'Salles, tables, menus, commandes, cuisine, service, addition.', 'AVAILABLE', ['pos'], 30),
    m('bar', 'Horevia Bar', 'Service au comptoir, notes nominatives, happy hours, tournées, encaissement.', 'AVAILABLE', ['pos'], 40),
    m('inventory', 'Horevia Inventory', 'Stocks, achats et approvisionnements.', 'AVAILABLE', [], 50),
    m('crm', 'Horevia CRM', 'Client unifié hébergement + points de vente, historique et dépenses consolidés, fidélité, segments, consentements, relances.', 'AVAILABLE', [], 60),
    m('analytics', 'Horevia Analytics', 'Vue consolidée hébergement + restauration + bar, ventes par point de vente, produit, heure et serveur, stocks et marges, rapports exportables.', 'AVAILABLE', [], 70),
    m('housekeeping', 'Horevia Housekeeping', 'Entretien et état des chambres.', 'COMING_SOON', ['hotel'], 80),
    m('booking', 'Horevia Booking', 'Moteur de réservation en ligne.', 'COMING_SOON', ['hotel'], 90),
    m('pay', 'Horevia Pay', 'Paiements en ligne et mobile money.', 'COMING_SOON', [], 100),
];
export const PLAN_CODES = [
    'HOTEL',
    'RESTAURANT',
    'BAR',
    'HOTEL_RESTAURANT',
    'SUITE',
    'CUSTOM',
];
export const PlanCode = z.enum(PLAN_CODES);
/** Plans commerciaux (hypothèse de départ, ajustables en base sans code). */
export const PLAN_CATALOG = [
    {
        code: 'HOTEL',
        name: 'Horevia Hotel',
        description: 'Gestion hôtelière seule.',
        modules: ['hotel'],
    },
    {
        code: 'RESTAURANT',
        name: 'Horevia Restaurant',
        description: 'Restaurant avec caisse.',
        modules: ['restaurant', 'pos'],
    },
    { code: 'BAR', name: 'Horevia Bar', description: 'Bar avec caisse.', modules: ['bar', 'pos'] },
    {
        code: 'HOTEL_RESTAURANT',
        name: 'Hotel + Restaurant',
        description: 'Hôtel, restaurant et caisse interconnectés.',
        modules: ['hotel', 'restaurant', 'pos'],
    },
    {
        code: 'SUITE',
        name: 'Horevia Suite',
        description: 'Tous les modules.',
        modules: [...MODULE_CODES],
    },
    {
        code: 'CUSTOM',
        name: 'Sur mesure',
        description: 'Modules choisis un à un (plateforme).',
        modules: [],
    },
];
export const SubscriptionStatus = z.enum(['TRIAL', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELLED']);
/** Statuts qui donnent accès aux modules (PAST_DUE = grâce, accès maintenu). */
export const SUBSCRIPTION_ACTIVE_STATUSES = [
    'TRIAL',
    'ACTIVE',
    'PAST_DUE',
];
export const Subscription = z.object({
    id: Uuid,
    planCode: PlanCode,
    planName: z.string(),
    status: SubscriptionStatus,
    startsAt: IsoDateTime,
    endsAt: IsoDateTime.nullable(),
    trialEndsAt: IsoDateTime.nullable(),
    version: Version,
});
export const PropertyModuleState = z.object({
    propertyId: Uuid,
    propertyCode: z.string(),
    propertyName: z.string(),
    enabled: z.boolean(),
    version: Version,
});
export const TenantModule = z.object({
    code: ModuleCode,
    name: z.string(),
    description: z.string(),
    status: ModuleStatus,
    dependsOn: z.array(ModuleCode),
    /** Inclus dans le plan de l'abonnement courant. */
    inPlan: z.boolean(),
    /** Activé pour le tenant (plan ou option). */
    enabled: z.boolean(),
    source: z.enum(['PLAN', 'ADDON']).nullable(),
    properties: z.array(PropertyModuleState),
});
export const TenantModulesOverview = z.object({
    subscription: Subscription.nullable(),
    modules: z.array(TenantModule),
});
export const UpdatePropertyModuleRequest = z.object({
    enabled: z.boolean(),
});
/** Module effectif d'un établissement = activé au tenant ET non désactivé pour l'établissement. */
export function effectiveModules(tenantModules, propertyOverrides) {
    return MODULE_CODES.filter((code) => tenantModules.has(code) && (propertyOverrides?.get(code) ?? true));
}
//# sourceMappingURL=modules.js.map